import * as React from "react";

/**
 * LogoHPPlus — minimal, scalable logo/icon for "healthypaws +"
 * - Vector only, no fonts; uses strokes/fills and currentColor for easy theming
 * - Variants: "capsule", "pawPlus", "shield", "circleMonogram"
 * - Size controlled via `size` prop (px) or via Tailwind classes on wrapper
 * - Color inherits from CSS `color`; override with `className` (e.g. text-sky-600)
 */

export type LogoHPPlusProps = React.SVGProps<SVGSVGElement> & {
  variant?: "capsule" | "pawPlus" | "shield" | "circleMonogram";
  size?: number;
};

export default function LogoHPPlus({
  variant = "capsule",
  size = 28,
  className,
  ...props
}: LogoHPPlusProps) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    role: "img",
    "aria-label": "HP plus logo",
    className,
    ...props,
  } as const;

  if (variant === "capsule") {
    // HP+ wordmark inside soft capsule. Geometric H/P built from paths; plus at end.
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="hpplus-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopOpacity="1" stopColor="currentColor" />
            <stop offset="100%" stopOpacity="0.7" stopColor="currentColor" />
          </linearGradient>
        </defs>
        <rect x="2" y="10" rx="16" ry="16" width="60" height="44" fill="none" stroke="currentColor" strokeWidth="4" />
        {/* H */}
        <path d="M16 20 v24 M16 32 h12 M28 20 v24" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        {/* P */}
        <path d="M36 20 v24 M36 20 h10 a8 8 0 0 1 0 16 h-10" stroke="url(#hpplus-gradient)" strokeWidth="4" strokeLinecap="round" fill="none" />
        {/* + */}
        <path d="M52 28 v12 M46 34 h12" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      </svg>
    );
  }

  if (variant === "pawPlus") {
    // Paw icon with integrated medical plus (clean, friendly). Works great as small icon.
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="paw-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopOpacity="1" stopColor="currentColor" />
            <stop offset="100%" stopOpacity="0.8" stopColor="currentColor" />
          </linearGradient>
        </defs>
        {/* Heel pad */}
        <ellipse cx="30" cy="38" rx="12" ry="10" fill="currentColor" opacity="0.3" />
        {/* Toes */}
        <circle cx="18" cy="28" r="4" fill="currentColor" />
        <circle cx="26" cy="24" r="5" fill="currentColor" />
        <circle cx="34" cy="24" r="5" fill="currentColor" />
        <circle cx="42" cy="28" r="4" fill="currentColor" />
        {/* Plus */}
        <g transform="translate(44,40)">
          <path d="M0 -6 v12 M-6 0 h12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
        </g>
      </svg>
    );
  }

  if (variant === "shield") {
    // Shield with HP+ monogram inside. Professional, medical feel.
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="shield-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopOpacity="1" stopColor="currentColor" />
            <stop offset="100%" stopOpacity="0.7" stopColor="currentColor" />
          </linearGradient>
        </defs>
        {/* Shield shape */}
        <path d="M32 4 L12 12 L12 28 Q12 40 20 48 Q28 56 32 60 Q36 56 44 48 Q52 40 52 28 L52 12 Z" 
              fill="url(#shield-gradient)" 
              stroke="currentColor" 
              strokeWidth="2" />
        {/* H */}
        <path d="M24 22 v16 M24 30 h8 M32 22 v16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
        {/* P */}
        <path d="M38 22 v16 M38 22 h6 a4 4 0 0 1 0 8 h-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
        {/* + */}
        <path d="M48 28 v8 M44 32 h8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    );
  }

  if (variant === "circleMonogram") {
    // HP+ in circular badge. Compact, works well as favicon/app icon.
    return (
      <svg {...common}>
        <defs>
          <linearGradient id="circle-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopOpacity="1" stopColor="currentColor" />
            <stop offset="100%" stopOpacity="0.8" stopColor="currentColor" />
          </linearGradient>
        </defs>
        {/* Circle */}
        <circle cx="32" cy="32" r="28" fill="url(#circle-gradient)" stroke="currentColor" strokeWidth="2" />
        {/* H */}
        <path d="M20 22 v20 M20 32 h12 M32 22 v20" stroke="white" strokeWidth="3" strokeLinecap="round" />
        {/* P */}
        <path d="M40 22 v20 M40 22 h8 a6 6 0 0 1 0 12 h-8" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none" />
        {/* + */}
        <circle cx="32" cy="48" r="8" fill="white" opacity="0.9" />
        <path d="M32 44 v8 M28 48 h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return null;
}

